import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, UtensilsCrossed } from 'lucide-react';
import { formatGs } from '@/utils/helpers';

// Play a short pleasant "ding" using Web Audio API (no external files needed)
function playSuccessSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Bell-like tone
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, ctx.currentTime); // A5
    osc1.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08); // E6

    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1100, ctx.currentTime);
    osc2.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.1);

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime + 0.05);
    osc1.stop(ctx.currentTime + 0.5);
    osc2.stop(ctx.currentTime + 0.5);

    // Cleanup
    setTimeout(() => ctx.close(), 600);
  } catch { /* Audio not supported or blocked - silently ignore */ }
}

export default function OrderSuccessOverlay({ order, onTrack, onMenu }) {
  const [show, setShow] = useState(false);
  const soundPlayed = useRef(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      setShow(true);
      // Play sound after user interaction (the confirm click counts)
      if (!soundPlayed.current) {
        soundPlayed.current = true;
        setTimeout(() => playSuccessSound(), 300);
      }
    });
  }, []);

  return createPortal(
    <div
      data-testid="order-success-overlay"
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center px-5 transition-all duration-500 ${show ? 'bg-zinc-950' : 'bg-zinc-950/0'}`}
    >
      {/* Decorative circles */}
      <div className={`absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-1000 ${show ? 'opacity-100' : 'opacity-0'}`}>
        <div className="absolute top-[15%] left-[10%] w-64 h-64 rounded-full bg-[#E84C1C]/5 blur-3xl" />
        <div className="absolute bottom-[20%] right-[5%] w-80 h-80 rounded-full bg-orange-500/3 blur-3xl" />
      </div>

      {/* Main content */}
      <div className={`relative text-center transition-all duration-700 delay-100 ${show ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-8'}`}>
        {/* Animated circle with chef icon */}
        <div className="relative mx-auto mb-6 w-28 h-28">
          {/* Outer ring animation */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 112 112">
            <circle
              cx="56" cy="56" r="52"
              fill="none"
              stroke="#E84C1C"
              strokeWidth="2"
              strokeDasharray="327"
              strokeDashoffset={show ? '0' : '327'}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 1.2s ease 0.3s' }}
            />
          </svg>
          {/* Inner circle */}
          <div className={`absolute inset-3 rounded-full bg-[#E84C1C]/10 flex items-center justify-center transition-all duration-700 delay-500 ${show ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              {/* Checkmark */}
              <path
                d="M12 22l7 7L32 15"
                stroke="#E84C1C"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="32"
                strokeDashoffset={show ? '0' : '32'}
                style={{ transition: 'stroke-dashoffset 0.6s ease 1s' }}
              />
            </svg>
          </div>
          {/* Floating emoji */}
          <div className={`absolute -top-2 -right-2 w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center transition-all duration-500 delay-1000 ${show ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}>
            <UtensilsCrossed size={18} className="text-[#E84C1C]" />
          </div>
        </div>

        {/* Text */}
        <h1 className={`text-2xl font-bold text-zinc-100 transition-all duration-500 delay-700 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          Pedido confirmado
        </h1>
        <p className={`text-sm text-zinc-400 mt-2 transition-all duration-500 delay-800 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          Estamos preparando tu pedido
        </p>

        {/* Order details card */}
        <div className={`mt-6 w-full max-w-xs mx-auto bg-zinc-900 border border-zinc-800/60 rounded-2xl p-5 space-y-3 text-left transition-all duration-600 delay-900 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex justify-between items-center">
            <span className="text-xs text-zinc-500">Pedido</span>
            <span className="text-base font-bold text-[#E84C1C]">#{order.id}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-zinc-500">Total</span>
            <span className="text-base font-semibold">{formatGs(order.total)}</span>
          </div>
          {order.branch_name && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-zinc-500">Sucursal</span>
              <span className="text-sm text-zinc-300">{order.branch_name}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-xs text-zinc-500">Tiempo estimado</span>
            <span className="text-sm text-zinc-300 font-medium">
              {order.order_type === 'pickup' ? '~35 min (retiro)' : '30 - 50 min'}
            </span>
          </div>
          {order.order_type === 'pickup' && (
            <div className="mt-1 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
              <p className="text-xs font-medium text-amber-400">Retiro en sucursal</p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className={`mt-6 w-full max-w-xs mx-auto space-y-2.5 transition-all duration-500 delay-1100 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <button
            data-testid="success-track-btn"
            onClick={onTrack}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-[#E84C1C] text-white font-bold hover:bg-[#d43f12] active:scale-[0.98] transition-all shadow-[0_0_24px_rgba(232,76,28,0.3)]"
          >
            Ver seguimiento <ArrowRight size={16} />
          </button>
          <button
            data-testid="success-menu-btn"
            onClick={onMenu}
            className="w-full py-2.5 rounded-2xl text-sm text-zinc-400 border border-zinc-800 hover:bg-zinc-800/50 transition-colors"
          >
            Volver al menú
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
